import React, { useState, useEffect, useRef } from 'react';
import { Input, ListGroup, ListGroupItem } from 'reactstrap';

const AddressAutocomplete = ({
  value,
  onChange,
  onAddressSelect,
  placeholder = "Enter address",
  className = "",
  ...inputProps
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [inputValue, setInputValue] = useState(value || '');
  const debounceTimer = useRef(null);
  const suggestionRefs = useRef([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);


  useEffect(() => {
    setInputValue(value || '');
  }, [value]);

  const searchAddresses = async (query) => {
    if (!query || query.length < 3) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setIsLoading(true);
    try {
      const response = await fetch(
        `/api/address-autocomplete?query=${encodeURIComponent(query)}`
      );

      if (response.ok) {
        const data = await response.json();
        setSuggestions(data.results || []);
        setShowSuggestions(true);
        setSelectedIndex(-1);
      }
    } catch (error) {
      console.error('Error fetching address suggestions:', error);
      setSuggestions([]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const query = e.target.value;
    setInputValue(query);
    onChange && onChange(e);

    // Clear previous timer
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }

    // Set new timer
    debounceTimer.current = setTimeout(() => {
      searchAddresses(query);
    }, 300);
  };

  const handleSuggestionClick = (suggestion) => {
    const address = suggestion.formatted;
    setInputValue(address);
    setShowSuggestions(false);
    setSuggestions([]);
    setSelectedIndex(-1);

    // Create synthetic event for onChange that matches expected BaseForm structure
    // The handleChange method destructures currentTarget as input, so disabled should be there
    const syntheticEvent = {
      currentTarget: {
        name: inputProps.name,
        value: address,
        disabled: false,
        type: 'text'
      }
    };
    onChange && onChange(syntheticEvent);

    // Call onAddressSelect with full address data
    if (onAddressSelect) {
      onAddressSelect({
        address: address,
        latitude: suggestion.lat,
        longitude: suggestion.lon,
        country: suggestion.country,
        city: suggestion.city || suggestion.municipality,
        postcode: suggestion.postcode
      });
    }
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || suggestions.length === 0) return;

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setSelectedIndex(prev =>
          prev < suggestions.length - 1 ? prev + 1 : prev
        );
        break;
      case 'ArrowUp':
        e.preventDefault();
        setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
        break;
      case 'Enter':
        e.preventDefault();
        if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
          handleSuggestionClick(suggestions[selectedIndex]);
        }
        break;
      case 'Escape':
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  const handleBlur = () => {
    // Delay hiding suggestions to allow click events
    setTimeout(() => {
      setShowSuggestions(false);
      setSelectedIndex(-1);
    }, 200);
  };

  const handleFocus = () => {
    if (suggestions.length > 0) {
      setShowSuggestions(true);
    }
  };

  return (
    <div className="position-relative">
      <Input
        {...inputProps}
        type="text"
        value={inputValue}
        onChange={handleInputChange}
        onKeyDown={handleKeyDown}
        onBlur={handleBlur}
        onFocus={handleFocus}
        placeholder={placeholder}
        className={className}
        autoComplete="off"
      />

      {isLoading && (
        <div className="position-absolute" style={{ top: '100%', left: 0, right: 0, zIndex: 1000 }}>
          <ListGroup>
            <ListGroupItem className="text-muted">
              <small>Searching addresses...</small>
            </ListGroupItem>
          </ListGroup>
        </div>
      )}

      {showSuggestions && suggestions.length > 0 && (
        <div className="position-absolute" style={{ top: '100%', left: 0, right: 0, zIndex: 1000, maxHeight: '200px', overflowY: 'auto' }}>
          <ListGroup>
            {suggestions.map((suggestion, index) => (
              <ListGroupItem
                key={`${suggestion.lat}-${suggestion.lon}-${index}`}
                action
                active={index === selectedIndex}
                onClick={() => handleSuggestionClick(suggestion)}
                className="cursor-pointer"
                style={{ cursor: 'pointer' }}
                ref={el => suggestionRefs.current[index] = el}
              >
                <div>
                  <strong>{suggestion.formatted}</strong>
                  {suggestion.country && (
                    <small className="d-block text-muted">
                      {[suggestion.city, suggestion.state, suggestion.country]
                        .filter(Boolean)
                        .join(', ')}
                    </small>
                  )}
                </div>
              </ListGroupItem>
            ))}
          </ListGroup>
        </div>
      )}
    </div>
  );
};

export default AddressAutocomplete;